// Faithful mirror of the LaTeX resume (../job-search-and-resumes/resumes/*.tex):
// keep roles, bullets, and numbers in lockstep. Shared fields (name, email,
// links) come from site.ts; only resume-specific data lives here.

export type ResumeJob = {
  role: string;
  org: string;
  location: string;
  period: string;
  bullets: readonly string[];
};

export type ResumeSchool = {
  school: string;
  location: string;
  detail: string;
  period: string;
};

export const resume = {
  phone: '614-619-3009',

  skills: {
    languages: ['Rust', 'Java', 'Python', 'C#', 'JavaScript', 'CSS', 'HTML'],
    technologies: [
      'React',
      'Tauri',
      'Electron',
      'WebSockets',
      'Selenium',
      'JUnit',
      'DBUnit',
      'Apache Airflow',
      'PostgreSQL',
      'Microsoft SQL Server (MSSQL)',
      'Hibernate',
      'Amazon Web Services (AWS)',
      'Prometheus',
      'Grafana',
      'Tailscale',
      'Git',
      'Linux',
    ],
  },

  experience: [
    {
      role: 'Junior Engineer',
      org: 'Illumynt',
      location: 'Columbus, OH',
      period: '11/2025 - Present',
      bullets: [
        'Built Rust software that recovers PSID-locked NVMe SSDs (up to 15 TB) wrongly scrapped by vendor erasure tools that mistook the PSID lock for sector read errors. Recovered roughly $2.4M across 8.2K+ drives.',
        'Drove the UX and Rust migration of the compute & consumer GPU, CPU, and memory testing platform, and delivered a client-facing demo supporting a multi-million-dollar GPU compute line of business.',
        'Solo-developed the company’s in-house platform for drive testing, verification, and erasure on a custom Linux build. Introduced and drove adoption of Rust, React, Tauri, and Electron in a previously Python-only team.',
        'Implemented low-level drive operations following recognized sanitization standards: pattern-based verification, erasure via low-level commands, and forensic verification of cryptographically erased drives.',
        'Built NetApp tooling that restores drive metadata to NetApp specifications corrupted by all three vendor tester platforms, plus an interface that simplifies serial and network configuration.',
        'Stood up fleet-wide monitoring of test benches with Tailscale, Prometheus, and Grafana: per-tester metrics and live dashboards that let engineering catch problems proactively.',
      ],
    },
    {
      role: 'Server Technician',
      org: 'Illumynt',
      location: 'Columbus, OH',
      period: '03/2025 - 11/2025',
      bullets: [
        'Tested and troubleshot servers, flashed firmware and reset management controllers (BMC, iDRAC, iLO, etc.). Reset networking equipment to factory defaults using CLI for various network operating systems.',
        'Recovered 99% of 300 8 TB SSDs ($200K) that froze during firmware-based erasure and were left undetectable by all three vendor tools; used Linux utilities and Bash scripting to recover them from scrap/chip-harvest.',
      ],
    },
    {
      role: 'Software Developer',
      org: 'The Ohio State University Wexner Medical Center',
      location: 'Columbus, OH',
      period: '10/2022 - 10/2023',
      bullets: [
        'Maintained a Java web application used for health records, patient scheduling and outreach programs.',
        "Reduced a disruptive nightly ETL's runtime from one hour to under 10 minutes through SQL optimization and multithreading, preventing downstream failures and manual reruns for over 20 jobs.",
        'Designed a dashboard that simplified resolving patient identity mismatches between human resources and a claims processing company in 90% of cases.',
        'Collaborated in migrating a legacy website with over 30,000 users to a modern tech stack using Java and React.',
        'Achieved over 95% test coverage with DBUnit, documenting comprehensive test cases.',
      ],
    },
    {
      role: 'Deployment Lead',
      org: 'JPMorgan Chase (contract)',
      location: 'Columbus, OH',
      period: '09/2019 - 09/2022',
      bullets: [
        'Made independent go/no-go and rollback calls on after-hours hardware deployments that routinely ran past 3 AM, with no escalation path available at that hour.',
        'Managed up to 5 concurrent hardware deployment projects (thin clients, teller workstations, printers, branch remodels) and served as the escalation point for bridge line, field, and L2 technicians.',
        'Served as the quality gate for field installs, reviewing each tech’s submitted photos for neat cabling, connected peripherals, and complete builds; subpar work that slipped through was charged back to our team.',
        'Sent a post-deployment status report to project managers and executives after each day’s cutover; wrote scopes of work from network scans (up to 100 sites/week).',
        'Recognized by the client for delivering multiple high-volume weeks at 100% first-visit completion.',
      ],
    },
    {
      role: 'Computer Technician',
      org: 'Arrow Electronics',
      location: 'Columbus, OH',
      period: '04/2016 - 09/2019',
      bullets: [
        'Led a team of 6 technicians on enterprise resale contracts with Fortune 100 companies; ran diagnostics, repairs, data sanitation, and firmware/BIOS upgrades at scale (5,000+ loose servers, storage arrays, networking gear, and 200+ fully-loaded cabinets).',
      ],
    },
  ] satisfies readonly ResumeJob[],

  education: [
    {
      school: 'Columbus State Community College',
      location: 'Columbus, OH',
      detail: "Associate's of Applied Science in Software Development (GPA: 4.0)",
      period: '08/2019 - 05/2021',
    },
    {
      school: 'The Ohio State University',
      location: 'Columbus, OH',
      detail: "Bachelor's of Arts in Linguistics (Unfinished, 190 credit hours completed)",
      period: '08/2004 - 06/2008',
    },
  ] satisfies readonly ResumeSchool[],
} as const;
